import * as React from "react";
import { Link } from "react-router-dom";
import "./contactus.css";
import Button from "@mui/material/Button";
import CssBaseline from "@mui/material/CssBaseline";
import TextField from "@mui/material/TextField";
import * as yup from "yup";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import { FormHelperText, Typography } from "@mui/material";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import ReCAPTCHA from "react-google-recaptcha";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import templeJson from "../../config/temple.json";
import useLoadContactus from "./customHook";
import { MuiTelInput } from "mui-tel-input";
import { parsePhoneNumberFromString } from "libphonenumber-js";

function ContactUs() {
  const validationSchema = yup.object().shape({
    name: yup.string().required("Please enter your name").max(50, "Name must be 50 characters or less"),
    phone: yup
    .string()
    .required("Mobile number is required")
    .test("is-valid-phone", "Invalid phone number for selected country", (value) => {
      try {
        const phoneNumber = parsePhoneNumberFromString(value || "");
        return phoneNumber && phoneNumber.isValid();
      } catch {
        return false;
      }
    }),
    email: yup
      .string()
      .email("Please enter a valid email address")
      .required("Please enter your email address")
      .max(50, "Email must be 50 characters or less"),
    subject: yup.string().required("Please select a subject"),
    message: yup.string().required("Please enter your message").max(400,"Message must be 400 characters or less"),
  });

  const {
    register,
    handleSubmit,
    reset,
    control,
    trigger,
    watch,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      phone: "",
    }
  });
  const {onSubmit,handleRecaptchaChange,recaptchaRef} = useLoadContactus(reset);

  const handleFormSubmit = async () => {
    const isValid = await trigger();

    if (!isValid) {
      const firstErrorField = Object.keys(errors)[0];

      const errorElement =
        document.querySelector(`[name="${firstErrorField}"]`) || // normal inputs
        document.querySelector(`[name="${firstErrorField}"] input`) || // nested inputs (like MuiTelInput)
        document.querySelector(`[name="${firstErrorField}"] select`) ||
        document.querySelector(`[name="${firstErrorField}"] textarea`);

      if (errorElement) {
        setTimeout(() => {
          errorElement.scrollIntoView({ behavior: "smooth", block: "center" });
          errorElement.focus({ preventScroll: true });
        }, 100);
      }

      return;
    }

    handleSubmit(onSubmit)();
  };


  return (
    <>
      <Container maxWidth={'xl'} className=" page-header py-4 breadcrumb-bg">
        <h1 className="text-center display-6 breadcrumb-h1">Contact Us</h1>
        <ol className="breadcrumb justify-content-center mb-0">
          <li className="breadcrumb-item">
            <Link to="/">Home</Link>
          </li>
          <li className="breadcrumb-item active">Contact Us</li>
        </ol>
      </Container>
      <Container className="py-5">
        <Grid className="text-center mb-4">
          <h6 className="section-title text-center text-primary text-uppercase title-tl">
              {templeJson["templeName"]}
          </h6>
          <h1 className="mb-3">
            <span className="text-secundary">Contact Us</span>
          </h1>
        </Grid>
        <Grid container>
          <Container component="main" maxWidth="sm">
            <Typography variant="body1">
              To contact the {templeJson["templeName"]}, please send an email to{" "}
              <a href={templeJson["contactEmail"]}>
              {templeJson["contactEmail"]}
              </a> or call <a href="tel:703-690-9355">703-690-9355</a>, Website issues
              or complete the contact form below. We will get back to you within
              48 hours.
            </Typography>
            <CssBaseline />
            <Box
              sx={{
                marginTop: 2,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
              }}
            >
              <Box
                component="form"
                noValidate
                onSubmit={(e) => e.preventDefault()}
                sx={{ mt: 3 }}
              >
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      {...register("name")}
                      autoComplete="given-name"
                      required
                      fullWidth
                      inputProps={{
                        maxLength:50
                      }}
                      label="Name"
                      autoFocus
                      error={errors?.name?.message}
                      helperText={errors?.name?.message}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Controller
                        control={control}
                        name="phone"
                        render={({ field }) => (
                          <MuiTelInput
                            {...field}
                            inputProps={{ name: "phone" }}
                            defaultCountry="US"
                            value={field.value}
                            onChange={(value) => {
                              field.onChange(value);
                            }}
                            sx={{ mt: "16px", mb: "8px" }}
                            fullWidth
                            label="Mobile *"
                            error={!!errors?.phone}
                            helperText={errors?.phone?.message}
                          />
                        )}
                    />
                  </Grid>
                  <Grid item xs={12} sx={{ marginBottom: "20px" }}>
                    <TextField
                      {...register("email")}
                      fullWidth
                      inputProps={{
                        maxLength:50
                      }}
                      label="Email"
                      autoComplete="email"
                      error={errors?.email?.message}
                      helperText={errors?.email?.message}
                    />
                  </Grid>
                </Grid>
                <FormControl fullWidth className="select-drp">
                  <InputLabel id="demo-simple-select-label">
                    Please Select Subject
                  </InputLabel>
                  <Select
                    {...register("subject")}
                    required
                    labelId="demo-simple-select-label"
                    id="demo-simple-select"
                    label="Please Select Subject"
                    error={Boolean(errors?.subject)}
                    helperText={errors?.subject?.message}
                  >
                    <MenuItem value={"Suggestions"}>Suggestions</MenuItem>
                    <MenuItem value={"Feedback"}>Feedback</MenuItem>
                    <MenuItem value={"Website Issues"}>Website Issues</MenuItem>
                    <MenuItem value={"Technical Comments"}>
                      Technical Comments
                    </MenuItem>
                    <MenuItem value={"Reservations"}>Reservations</MenuItem>
                    <MenuItem value={"Other"}>Other</MenuItem>
                  </Select>
                </FormControl>
                {errors?.subject ? (
                  <FormHelperText className="m-3" error>
                    {errors?.subject.message}
                  </FormHelperText>
                ) : null}{" "}
                <TextField
                  required
                  {...register("message")}
                  multiline
                  minRows={5}
                  inputProps={{
                    maxLength:400
                  }}
                  fullWidth
                  placeholder="Message"
                  className="text-area"
                  error={errors?.message?.message}
                  helperText={errors?.message?.message}
                />
                {errors?.message ? (
                  <FormHelperText className="m-3" error>
                    {errors?.message.message}
                  </FormHelperText>
                ) : null}{" "}
                <div className="mt-2">
                  <ReCAPTCHA
                    sitekey="6Lf09rwpAAAAAIpAMtEjoqFyZIECcrK6A-HU7Uu5"
                    ref={recaptchaRef}
                    onChange={handleRecaptchaChange}
                  />
                </div>
                <Button
                  className="cont-btn"
                  type="button"
                  onClick={handleFormSubmit}
                  variant="contained"
                  sx={{ mt: 3, mb: 2, p: 2 }}
                >
                  Submit
                </Button>
              </Box>
            </Box>
          </Container>
        </Grid>
      </Container>
    </>
  );
}

export default ContactUs;
